import {
  AuditAction,
  AuditEntityType,
  AuditModule,
  SortDirection,
} from '@trulyinbox/types';
import { apiFetch } from './api';

// ── Domain vocabularies (single source of truth) ────────────────────────────
//
// `Object.values` on a string enum yields exactly the value strings (no numeric
// reverse-mapping). The URL search-schema validates its `module`/`action`/
// `entityType` filters against these FULL enum arrays — so a saved/deep link
// with a valid value still round-trips even if the workspace currently has no
// rows for it, and an invalid one is dropped here rather than 400-ing the list
// request (the backend DTO is `@IsEnum`-validated on all three).
// The module/action pickers narrow further to `GET /audit-logs/filters` (only
// values actually present) so they stay short; the entity-type picker has no
// such facet and lists the full 7-value enum.
export const AUDIT_MODULES: AuditModule[] = Object.values(AuditModule);
export const AUDIT_ACTIONS: AuditAction[] = Object.values(AuditAction);
export const AUDIT_ENTITY_TYPES: AuditEntityType[] = Object.values(AuditEntityType);

// ── Wire types ───────────────────────────────────────────────────────────────

/**
 * A single audit-log entry. `before`/`after`/`metadata` carry only the changed
 * fields (not whole entities). Actor fields are null for system/webhook-driven
 * changes. `createdAt` arrives as an ISO string (`apiFetch` returns the raw JSON
 * body); convert via `lib/date`'s `toDate` at the render boundary.
 */
export type AuditLogDto = {
  id: number;
  orgId: number;
  workspaceId: number | null;
  actorUserId: number | null;
  actorName: string | null;
  actorRole: string | null;
  module: AuditModule;
  action: AuditAction;
  entityType: AuditEntityType;
  entityId: string | null;
  entityLabel: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
};

/** Offset-paginated page of audit entries (`GET /audit-logs`). */
export type PaginatedAuditLogs = {
  data: AuditLogDto[];
  total: number;
  page: number;
  limit: number;
};

/** Distinct filter values present, for populating the drawer dropdowns
 * (`GET /audit-logs/filters`). */
export type AuditLogFilterOptions = {
  modules: AuditModule[];
  actions: AuditAction[];
  actors: { id: number; name: string }[];
};

export type ListAuditLogsParams = {
  /** Module filter (OR match). One `module=` param per value. */
  module?: AuditModule[];
  /** Action filter (OR match). One `action=` param per value. */
  action?: AuditAction[];
  actorUserId?: number;
  entityType?: AuditEntityType;
  /** ISO `YYYY-MM-DD` lower/upper bound on `createdAt`. */
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  /** Rows per page (server caps at 100). */
  limit?: number;
  sort?: SortDirection;
};

// ── Request functions ────────────────────────────────────────────────────────

/**
 * Fetch a paginated, filtered slice of the org/workspace's audit log. Offset
 * pagination (`page`/`limit`), not cursor. `module`/`action` are multi-select:
 * each selected value is appended as a repeated param (OR-matched within the
 * dimension); an empty set sends nothing. Inactive scalar filters are omitted so
 * the request — and its cache key — mirrors exactly what the URL carries.
 */
export async function listAuditLogs(
  params: ListAuditLogsParams = {},
): Promise<PaginatedAuditLogs> {
  const sp = new URLSearchParams();
  for (const m of params.module ?? []) sp.append('module', m);
  for (const a of params.action ?? []) sp.append('action', a);
  if (params.actorUserId !== undefined) sp.set('actorUserId', String(params.actorUserId));
  if (params.entityType) sp.set('entityType', params.entityType);
  if (params.dateFrom) sp.set('dateFrom', params.dateFrom);
  if (params.dateTo) sp.set('dateTo', params.dateTo);
  if (params.page !== undefined) sp.set('page', String(params.page));
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  if (params.sort) sp.set('sort', params.sort);
  const qs = sp.toString();
  return apiFetch<PaginatedAuditLogs>(`/audit-logs${qs ? `?${qs}` : ''}`);
}

/** Distinct modules/actions/actors present, for the filter drawer dropdowns. */
export async function getAuditLogFilters(): Promise<AuditLogFilterOptions> {
  return apiFetch<AuditLogFilterOptions>('/audit-logs/filters');
}

/** One audit entry by id. Throws `ApiError` w/ code `190201` when missing or
 * outside the caller's scope. */
export async function getAuditLog(id: number): Promise<AuditLogDto> {
  return apiFetch<AuditLogDto>(`/audit-logs/${id}`);
}
